/**
 * Área FICHA · HISTÓRICO: as leituras que a linha do tempo do atendimento junta.
 *
 * É a `carregarTimelineCliente` do legado (`admin.html:5656` a `5741`), só na parte que vai ao
 * banco. Montar os eventos, escrever cada frase e ordenar é regra pura, e mora na tela
 * (`abas/timeline/eventos.ts`), sem import de banco.
 *
 * O LEGADO IGNORA A FALHA de cada leitura (`historico.data || []`, `geral.data?.criado_em`): uma
 * fonte que falha só deixa de somar eventos. Por isso as funções devolvem as leituras cruas, e a
 * tela decide igual.
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum (ver PADRAO.md).
import {
  clienteDe,
  comEscopo,
  executar,
  type Escopo,
  type FonteDeDados,
  type LinhaCompromissosAgenda,
  type LinhaJornadaClienteHistorico,
  type LinhaLeadsFunilStatusHistorico,
  type LinhaMensagens,
  type LinhaPagamentos,
  type LinhaParcelas,
  type Resultado,
} from '../nucleo.js';

/** Só o `criado_em`, que é o que o legado lê dos quatro questionários e checklists. */
export interface SoCriadoEm {
  readonly criado_em: string;
}

/** As colunas que cada leitura pede, uma a uma como no `select` do legado. */
export type EventoDeJornada = Pick<LinhaJornadaClienteHistorico, 'etapa_anterior' | 'etapa_nova' | 'mudado_em'>;
export type MensagemDoHistorico = Pick<LinhaMensagens, 'remetente' | 'conteudo' | 'criado_em'>;
export type CompromissoDoHistorico = Pick<LinhaCompromissosAgenda, 'titulo' | 'data_hora_inicio' | 'criado_em'>;
export type PagamentoDoHistorico = Pick<LinhaPagamentos, 'id' | 'valor_total' | 'criado_em'>;
export type StatusDoHistorico = Pick<LinhaLeadsFunilStatusHistorico, 'status_anterior' | 'status_novo' | 'mudado_em'>;
export type ParcelaPagaDoHistorico = Pick<LinhaParcelas, 'numero' | 'valor' | 'data_pagamento'>;

/** As seis leituras por escopo, na ordem do `Promise.all` do legado (`admin.html:5668`). */
export interface LeiturasDaFicha {
  readonly historico: Resultado<readonly EventoDeJornada[] | null>;
  readonly geral: Resultado<SoCriadoEm | null>;
  readonly avaliativo: Resultado<SoCriadoEm | null>;
  readonly atendimento: Resultado<SoCriadoEm | null>;
  readonly milhoes: Resultado<SoCriadoEm | null>;
  readonly mensagens: Resultado<readonly MensagemDoHistorico[] | null>;
}

/**
 * As seis leituras da cliente e do lead, em paralelo como no legado.
 * Legado: `admin.html:5668` a `5675`:
 *   jornada_cliente_historico  select('etapa_anterior, etapa_nova, mudado_em')   (lista)
 *   questionario_geral         select('criado_em')                              maybeSingle
 *   questionario_avaliativo    select('criado_em')                              maybeSingle
 *   checklist_atendimento      select('criado_em')                              maybeSingle
 *   checklist_milhoes          select('criado_em')                              maybeSingle
 *   mensagens                  select('remetente, conteudo, criado_em')         (lista)
 * todas com `comEscopo`.
 */
export async function lerLeiturasDaFicha(fonte: FonteDeDados, escopo: Escopo): Promise<LeiturasDaFicha> {
  // O cliente uma vez, para as seis consultas saírem do mesmo lugar.
  const sb = clienteDe(fonte);
  // Em paralelo, como o `Promise.all` do legado: nenhuma depende da outra.
  const [historico, geral, avaliativo, atendimento, milhoes, mensagens] = await Promise.all([
    executar<readonly EventoDeJornada[] | null>(
      comEscopo(sb.from('jornada_cliente_historico').select('etapa_anterior, etapa_nova, mudado_em'), escopo),
    ),
    executar<SoCriadoEm | null>(comEscopo(sb.from('questionario_geral').select('criado_em'), escopo).maybeSingle()),
    executar<SoCriadoEm | null>(comEscopo(sb.from('questionario_avaliativo').select('criado_em'), escopo).maybeSingle()),
    executar<SoCriadoEm | null>(comEscopo(sb.from('checklist_atendimento').select('criado_em'), escopo).maybeSingle()),
    executar<SoCriadoEm | null>(comEscopo(sb.from('checklist_milhoes').select('criado_em'), escopo).maybeSingle()),
    executar<readonly MensagemDoHistorico[] | null>(
      comEscopo(sb.from('mensagens').select('remetente, conteudo, criado_em'), escopo),
    ),
  ]);
  return { historico, geral, avaliativo, atendimento, milhoes, mensagens };
}

/** As três leituras por lead, na ordem do segundo `Promise.all` do legado (`admin.html:5697`). */
export interface LeiturasDoLead {
  readonly compromissos: Resultado<readonly CompromissoDoHistorico[] | null>;
  readonly pagamentos: Resultado<readonly PagamentoDoHistorico[] | null>;
  readonly statusHistorico: Resultado<readonly StatusDoHistorico[] | null>;
}

/**
 * As três leituras que só existem com lead aberto, em paralelo como no legado.
 * Legado: `admin.html:5697` a `5701`:
 *   compromissos_agenda           select('titulo, data_hora_inicio, criado_em')      eq lead_id
 *   pagamentos                    select('id, valor_total, criado_em')               eq lead_id
 *   leads_funil_status_historico  select('status_anterior, status_novo, mudado_em')  eq lead_id
 * Sem `comEscopo`: estas tabelas não têm `user_id`, e o filtro é só o lead.
 */
export async function lerLeiturasDoLead(fonte: FonteDeDados, leadId: number): Promise<LeiturasDoLead> {
  const sb = clienteDe(fonte);
  const [compromissos, pagamentos, statusHistorico] = await Promise.all([
    executar<readonly CompromissoDoHistorico[] | null>(
      sb.from('compromissos_agenda').select('titulo, data_hora_inicio, criado_em').eq('lead_id', leadId),
    ),
    executar<readonly PagamentoDoHistorico[] | null>(
      sb.from('pagamentos').select('id, valor_total, criado_em').eq('lead_id', leadId),
    ),
    executar<readonly StatusDoHistorico[] | null>(
      sb.from('leads_funil_status_historico').select('status_anterior, status_novo, mudado_em').eq('lead_id', leadId),
    ),
  ]);
  return { compromissos, pagamentos, statusHistorico };
}

/**
 * As parcelas já pagas dos pagamentos do lead.
 * Legado: `admin.html:5718`, `sb.from('parcelas').select('numero, valor, data_pagamento')
 * .in('pagamento_id', pagIds).not('data_pagamento', 'is', null)`.
 *
 * Parcela não tem `lead_id`, só `pagamento_id`, e por isso a leitura depende dos pagamentos
 * (`5714` e `5715`). O legado só consulta quando há pelo menos um pagamento (`5717`), e quem
 * decide isso é a tela, com a lista na mão.
 */
export function lerParcelasPagas(
  fonte: FonteDeDados,
  pagamentoIds: readonly number[],
): Promise<Resultado<readonly ParcelaPagaDoHistorico[] | null>> {
  return executar<readonly ParcelaPagaDoHistorico[] | null>(
    clienteDe(fonte)
      .from('parcelas')
      .select('numero, valor, data_pagamento')
      .in('pagamento_id', [...pagamentoIds])
      // Só as pagas: parcela em aberto não é evento do histórico.
      .not('data_pagamento', 'is', null),
  );
}
